import { INSTALL_PROMPT_EVENT } from "./constants";
import { isBrowser } from "./supports";

export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<(installable: boolean) => void>();
let isBound = false;

function emit(installable: boolean): void {
  for (const listener of listeners) {
    listener(installable);
  }
}

function bindInstallPrompt(): void {
  if (!isBrowser() || isBound) {
    return;
  }

  window.addEventListener(INSTALL_PROMPT_EVENT, (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    emit(true);
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    emit(false);
  });

  isBound = true;
}

export function initPWAInstallTracking(): void {
  bindInstallPrompt();
}

export function subscribeToInstallPrompt(listener: (installable: boolean) => void): () => void {
  bindInstallPrompt();
  listeners.add(listener);
  listener(Boolean(deferredPrompt));

  return () => {
    listeners.delete(listener);
  };
}

export async function promptForInstall(): Promise<boolean> {
  if (!deferredPrompt) {
    return false;
  }

  await deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice;
  const accepted = choice.outcome === "accepted";

  if (accepted) {
    deferredPrompt = null;
    emit(false);
  }

  return accepted;
}
